import { Injectable, inject } from '@angular/core';
import { CmUiCompoundFilterObjectFieldOption } from '@cm/ui-modules';
import {
  FieldFragment,
  GetFieldOrderGQL,
  GetFieldsGQL,
  GetSegmentKeyTypeElementsGQL,
  GetSegmentKeyTypeElementsQueryVariables,
  GetSegmentKeyTypesGQL,
  GetSegmentKeyTypesQueryVariables,
  SegmentKeyTypeElement,
  Type,
} from '@graphql';
import { FieldWithValue } from '@shared-models';
import { ObjectName } from '@view-model';
import { iif, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

// There are fields that user cannot fill, API should serve only insertable fields
const NON_INSERTABLE: string[] = [
  'fullName',
  'sourceformId',
  'graphQLEnumDefinition',
  'graphQLFieldDefinition',
  'phoneNumberStatus',
  'emailStatus',
];
const NON_INSERTABLE_LEAD: string[] = [
  'fullName',
  'sourceformId',
  'graphQLEnumDefinition',
  'graphQLFieldDefinition',
  'externalId',
];
const NON_INSERTABLE_CUSTOM_FIELD: string[] = [
  'id',
  'fullName',
  'sourceformId',
  'graphQLEnumDefinition',
  'graphQLFieldDefinition',
  'phoneNumberStatus',
  'emailStatus',
];

@Injectable({
  providedIn: 'root',
})
export class GetFieldsService {
  private readonly getFieldsGQL = inject(GetFieldsGQL);
  private readonly getSegmentKeyTypeElements = inject(GetSegmentKeyTypeElementsGQL);
  private readonly getSegmentKeyTypesGQL = inject(GetSegmentKeyTypesGQL);
  private readonly translateService = inject(TranslateService);
  private readonly getFieldOrderGQL = inject(GetFieldOrderGQL);

  getFields(objectName: ObjectName): Observable<FieldFragment[]> {
    return this.getFieldsGQL
      .fetch({ variables: { objectName }, fetchPolicy: 'no-cache', errorPolicy: 'all' })
      .pipe(
        catchError((error) => throwError(() => error)),
        map((response) => response.data?.__type?.fields || []),
      );
  }
  getSegmentKeyTypes(variables: GetSegmentKeyTypesQueryVariables): Observable<Type[]> {
    return this.getSegmentKeyTypesGQL
      .watch({ variables, errorPolicy: 'all', fetchPolicy: 'network-only' })
      .valueChanges.pipe(
        filter((result) => !result.loading),
        switchMap((result) =>
          iif(
            () => !result.error,
            of(
              (result.data!.getSegmentKeyTypes || []).filter((item): item is Type => item != null),
            ),
            throwError(result.error),
          ),
        ),
      );
  }
  getTypesAndKeys(
    variables: GetSegmentKeyTypeElementsQueryVariables,
  ): Observable<SegmentKeyTypeElement[]> {
    return this.getSegmentKeyTypeElements
      .watch({ variables, errorPolicy: 'all', fetchPolicy: 'network-only' })
      .valueChanges.pipe(
        filter((result) => !result.loading),
        switchMap((result) =>
          iif(
            () => !result.error,
            of(
              (result.data!.segmentKeyTypeElements || []).filter(
                (item): item is SegmentKeyTypeElement => item != null,
              ),
            ),
            throwError(result.error),
          ),
        ),
      );
  }
  sortFields(objectName: ObjectName, fields: FieldWithValue[]): Observable<FieldWithValue[]> {
    const sortArray = (fieldArray: string[]) => (a: any, b: any) =>
      fieldArray.indexOf(b.name) - fieldArray.indexOf(a.name);
    return this.fetchFieldsOrder(objectName).pipe(
      catchError(() => of([])),
      map((result) => fields.sort(sortArray(result)).reverse()),
    );
  }

  getFieldsToObjectFieldForSegmentFilters(
    objectName: ObjectName,
  ): Observable<CmUiCompoundFilterObjectFieldOption[]> {
    return of([{ value: 'testField', kind: 'TEXT', label: 'testowe pole ' }]);
  }

  fetchFieldsOrder(dtoClassName: ObjectName): Observable<string[]> {
    return this.getFieldOrderGQL
      .fetch({ variables: { dtoClassName }, fetchPolicy: 'no-cache', errorPolicy: 'all' })
      .pipe(
        map((response) =>
          (response.data?.getFieldOrder?.objectFieldOrderList || []).filter(
            (item): item is string => item != null,
          ),
        ),
      );
  }

  getFieldsToObjectFieldOption(
    objectName: ObjectName,
  ): Observable<CmUiCompoundFilterObjectFieldOption[]> {
    return this.getNonInsertable(objectName).pipe(
      map((fields) => fieldsToFieldOptions(objectName, fields, this.translateService)),
    );
  }

  getNonInsertable(objectName: ObjectName): Observable<FieldFragment[]> {
    let nonInsertableFields: string[];
    if (objectName === 'Lead') {
      nonInsertableFields = NON_INSERTABLE_LEAD;
    } else if (objectName === 'CustomFieldDefinition') {
      nonInsertableFields = NON_INSERTABLE_CUSTOM_FIELD;
    } else {
      nonInsertableFields = NON_INSERTABLE;
    }

    return this.getFields(objectName).pipe(
      map((fields) => fields.filter((field) => !nonInsertableFields.includes(field.name))),
    );
  }
}

function fieldsToFieldOptions(
  objectName: string,
  fields: FieldFragment[],
  translateService: TranslateService,
): CmUiCompoundFilterObjectFieldOption[] {
  return fields.map((field) => ({
    label: translateService.instant('API.fields.' + objectName + '.' + field.name),
    value: field.name,
    kind: field.type && field.type.name === 'Int' ? 'NUMBER' : field.type.kind,
    name: field.type?.name,
  }));
}
